"use client";

import { useState } from "react";
import Link from "next/link";
import { Settings, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCookieConsent, type CookieConsent } from "@/lib/cookie-consent";

export function CookieBanner() {
  const { showBanner, acceptAll, rejectAll, acceptSelected } =
    useCookieConsent();
  const [showDetails, setShowDetails] = useState(false);
  const [selected, setSelected] = useState<CookieConsent>({
    necessary: true,
    statistics: false,
    marketing: false,
  });

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-100 flex items-end justify-center p-4 sm:items-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />

      {/* Banner */}
      <div className="relative w-full max-w-lg rounded-xl border border-border bg-card p-6 shadow-2xl animate-fade-in-up sm:max-w-xl">
        {/* Header */}
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground font-serif">
              Cookie-Einstellungen
            </h2>
            <p className="text-xs text-muted-foreground">
              Ihre Privatsphäre ist uns wichtig
            </p>
          </div>
        </div>

        {/* Text */}
        <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
          Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer
          Website zu bieten. Einige Cookies sind notwendig, andere helfen uns,
          unsere Website zu verbessern und Ihnen relevante Inhalte anzuzeigen.{" "}
          <Link
            href="/datenschutz"
            rel="nofollow"
            className="underline underline-offset-2 hover:text-primary transition-colors"
          >
            Datenschutzerklärung
          </Link>{" "}
          ·{" "}
          <Link
            href="/cookies"
            className="underline underline-offset-2 hover:text-primary transition-colors"
          >
            Cookie-Richtlinie
          </Link>
        </p>

        {/* Details toggle */}
        {showDetails && (
          <div className="mb-5 space-y-3 rounded-lg border border-border bg-muted/50 p-4">
            {/* Necessary */}
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked
                disabled
                className="mt-1 h-4 w-4 rounded border-border accent-primary"
              />
              <div>
                <span className="text-sm font-medium text-foreground">
                  Notwendig
                </span>
                <p className="text-xs text-muted-foreground">
                  Diese Cookies sind für die Grundfunktionen der Website
                  erforderlich und können nicht deaktiviert werden.
                </p>
              </div>
            </label>

            {/* Statistics */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.statistics}
                onChange={(e) =>
                  setSelected((s) => ({ ...s, statistics: e.target.checked }))
                }
                className="mt-1 h-4 w-4 rounded border-border accent-primary"
              />
              <div>
                <span className="text-sm font-medium text-foreground">
                  Statistik
                </span>
                <p className="text-xs text-muted-foreground">
                  Helfen uns zu verstehen, wie Besucher mit unserer Website
                  interagieren, indem Informationen anonym gesammelt werden
                  (z.B. Google Analytics).
                </p>
              </div>
            </label>

            {/* Marketing */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.marketing}
                onChange={(e) =>
                  setSelected((s) => ({ ...s, marketing: e.target.checked }))
                }
                className="mt-1 h-4 w-4 rounded border-border accent-primary"
              />
              <div>
                <span className="text-sm font-medium text-foreground">
                  Marketing
                </span>
                <p className="text-xs text-muted-foreground">
                  Werden verwendet, um Besuchern relevante Werbung und
                  Marketing-Kampagnen anzuzeigen (z.B. Google Ads, Facebook
                  Pixel).
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          {showDetails ? (
            <>
              <Button
                onClick={() => acceptSelected(selected)}
                variant="outline"
                className="w-full sm:flex-1 cursor-pointer"
              >
                Auswahl speichern
              </Button>
              <Button
                onClick={acceptAll}
                className="w-full sm:flex-1 cursor-pointer"
              >
                Alle akzeptieren
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={rejectAll}
                variant="outline"
                className="w-full sm:flex-1 cursor-pointer"
              >
                Nur notwendige
              </Button>
              <Button
                onClick={acceptAll}
                className="w-full sm:flex-1 cursor-pointer"
              >
                Alle akzeptieren
              </Button>
              <Button
                className="cursor-pointer"
                onClick={() => setShowDetails(true)}
                variant="ghost"
                size="icon"
                aria-label="Cookie-Einstellungen anpassen"
              >
                <Settings className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
